import React, { useId, useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Search, ChevronDown, Check, X } from 'lucide-react';
import { cn } from '../../lib/utils';
import { useExclusiveDropdown } from '../../lib/dropdowns';

interface Option {
  id?: string;
  value?: string;
  label: string;
  subLabel?: string;
}

interface SearchableSelectProps {
  options: Option[];
  value: string;
  onChange: (e: { target: { value: string } }) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  label?: string; // For display on top like standard components
  containerClassName?: string;
  portal?: boolean;
}

export function SearchableSelect({
  options,
  value,
  onChange,
  placeholder = "Select option...",
  className,
  disabled = false,
  label: topLabel,
  containerClassName,
  portal = true
}: SearchableSelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const instanceId = useId();
  const announceDropdownOpen = useExclusiveDropdown(
    `searchable-select-${instanceId}`,
    () => setIsOpen(false),
  );
  const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const normalizedOptions = options.map(opt => ({
    id: opt.id || opt.value || '',
    label: opt.label,
    subLabel: opt.subLabel
  }));

  const selectedOption = normalizedOptions.find(opt => opt.id === value);

  const updateDropdownPosition = () => {
    if (!portal || !triggerRef.current) return;

    const rect = triggerRef.current.getBoundingClientRect();
    const gap = 4;
    const viewportPadding = 12;
    const preferredHeight = 360;
    const menuWidth = Math.min(
      Math.max(rect.width, 240),
      window.innerWidth - viewportPadding * 2
    );
    const left = Math.min(
      Math.max(rect.left, viewportPadding),
      window.innerWidth - menuWidth - viewportPadding
    );
    const measuredHeight = dropdownRef.current?.getBoundingClientRect().height || preferredHeight;
    const spaceBelow = window.innerHeight - rect.bottom - gap;
    const spaceAbove = rect.top - gap;
    const openAbove = spaceBelow < measuredHeight && spaceAbove > spaceBelow;
    const availableSpace = openAbove ? spaceAbove : spaceBelow;
    const maxHeight = Math.max(160, Math.min(preferredHeight, availableSpace));
    const menuHeight = Math.min(measuredHeight, maxHeight);

    setDropdownStyle({
      left,
      width: menuWidth,
      top: openAbove ? rect.top - menuHeight - gap : rect.bottom + gap,
      maxHeight
    });
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        containerRef.current &&
        !containerRef.current.contains(target) &&
        !dropdownRef.current?.contains(target)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (!isOpen || !portal) return;

    updateDropdownPosition();
    const frame = window.requestAnimationFrame(updateDropdownPosition);
    const handleViewportChange = () => updateDropdownPosition();
    window.addEventListener('resize', handleViewportChange);
    window.addEventListener('scroll', handleViewportChange, true);

    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener('resize', handleViewportChange);
      window.removeEventListener('scroll', handleViewportChange, true);
    };
  }, [isOpen, portal, searchTerm, normalizedOptions.length]);

  const filteredOptions = normalizedOptions.filter(opt =>
    opt.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (opt.subLabel && opt.subLabel.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const handleSelect = (id: string) => {
    onChange({ target: { value: id } });
    setIsOpen(false);
    setSearchTerm('');
  };

  return (
    <div className={cn("relative space-y-1.5", containerClassName)} ref={containerRef}>
      {topLabel && (
        <label className="text-[10px] uppercase tracking-widest font-bold opacity-40">{topLabel}</label>
      )}
      <div
        ref={triggerRef}
        className={cn(
          "flex h-9 items-center justify-between w-full bg-background border border-input px-3 rounded-md cursor-pointer transition-[border-color,box-shadow] text-foreground",
          className,
          isOpen ? "border-ring ring-2 ring-ring/20" : "hover:border-primary/40",
          disabled && "opacity-50 cursor-not-allowed pointer-events-none"
        )}
        onClick={() => {
          if (!disabled) {
            const nextOpen = !isOpen;
            setDropdownStyle(null);
            if (nextOpen) announceDropdownOpen();
            setIsOpen(nextOpen);
            if (nextOpen) {
              setTimeout(() => {
                updateDropdownPosition();
                inputRef.current?.focus();
              }, 0);
            }
          }
        }}
      >
        <span className={cn("text-sm truncate font-bold", !selectedOption && "opacity-40 font-normal")}>
          {selectedOption ? selectedOption.label : placeholder}
        </span>
        <div className="flex items-center gap-1 text-muted-foreground">
          {value && (
            <X 
              className="w-4 h-4 hover:text-red-500 transition-colors" 
              onClick={(e) => {
                e.stopPropagation();
                onChange({ target: { value: '' } });
              }}
            />
          )}
          <ChevronDown className={cn("w-4 h-4 transition-transform", isOpen && "rotate-180")} />
        </div>
      </div>

      {(() => {
        if (!isOpen) return null;

        const dropdownMenu = (
          <div
            ref={dropdownRef}
            className={cn(
              portal ? "fixed z-[10000]" : "absolute z-[150] mt-2 w-full",
              "bg-card text-foreground border border-border rounded-lg shadow-lg ring-1 ring-black/5 overflow-hidden animate-in fade-in duration-150"
            )}
            style={portal ? (dropdownStyle || { visibility: 'hidden' }) : undefined}
          >
            <div className="p-2.5 border-b border-border bg-muted/60">
              <div className="relative flex items-center bg-card border border-border rounded-lg focus-within:border-primary focus-within:ring-2 focus-within:ring-primary/15 transition-colors">
                <Search className="absolute left-3 w-4 h-4 text-muted-foreground pointer-events-none" />
                <input
                  ref={inputRef}
                  type="text"
                  className="w-full pl-10 pr-3 py-2 bg-transparent text-sm font-medium text-foreground placeholder:text-muted-foreground outline-none"
                  placeholder="Type to search..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                />
              </div>
            </div>
            <div className="max-h-[300px] overflow-y-auto custom-scrollbar p-1.5">
              {filteredOptions.length > 0 ? (
                filteredOptions.map((opt) => (
                  <div
                    key={opt.id}
                    className={cn(
                      "flex items-center justify-between gap-3 px-3 py-2 rounded-md cursor-pointer transition-colors mb-0.5 last:mb-0",
                      value === opt.id
                        ? "bg-primary text-primary-foreground shadow-sm"
                        : "text-foreground hover:bg-primary/10 hover:text-primary"
                    )}
                    onClick={() => handleSelect(opt.id)}
                  >
                    <div>
                      <p className="text-sm font-medium truncate">{opt.label}</p>
                      {opt.subLabel && <p className={cn("text-[10px] uppercase tracking-widest text-muted-foreground font-medium truncate", value === opt.id && "text-primary-foreground/75")}>{opt.subLabel}</p>}
                    </div>
                    {value === opt.id && <Check className="w-4 h-4" />}
                  </div>
                ))
              ) : (
                <div className="p-8 text-center text-muted-foreground italic serif text-sm">
                  No matching options found
                </div>
              )}
            </div>
          </div>
        );

        return portal && typeof document !== 'undefined'
          ? createPortal(dropdownMenu, document.body)
          : dropdownMenu;
      })()}
    </div>
  );
}
